<!-- Peek tab: always present when the sheet is closed -->
@if (!chat.isOpen()) {
  <button class="peek-tab" (click)="chat.open()" aria-label="Open Vega" data-tour-step="global.assistant">
    <tp-vega-avatar [size]="22" />
    <span class="peek-label">Vega</span>
    @if (chat.unreadCount() > 0) {
      <span class="unread-dot peek-unread" aria-label="Unread scheduled results"></span>
    }
  </button>
}

@if (chat.isOpen()) {
  <!-- global.assistant-open: the panel is open (walkthrough steps that happen in this chat). -->
  <section
    class="sheet"
    #sheetEl
    data-tour-step="global.assistant-open"
    [class.sheet--floating]="chat.isFloating() && !chat.fullScreen()"
    [class.sheet--edge]="activeDockEdge()"
    [class.sheet--fullscreen]="chat.fullScreen()"
    [style.left.px]="floatingRect()?.left"
    [style.top.px]="floatingRect()?.top"
    [style.width.px]="floatingRect()?.width"
    [style.height.px]="floatingRect()?.height"
    [style.--tp-sheet-extra]="sheetExtra() + 'px'"
    role="dialog"
    aria-label="Vega"
  >
    <!-- The header is the drag handle: pulling it off dock undocks the panel,
         and it keeps moving the free window afterwards. -->
    <!-- global.assistant-fresh: no earlier conversation is loaded (a walkthrough's "start a fresh chat" step). -->
    <header
      class="sheet-header"
      [attr.data-tour-step]="freshChat() ? 'global.assistant-fresh' : null"
      (pointerdown)="onHeaderPointerDown($event)"
      (pointermove)="onHeaderPointerMove($event)"
      (pointerup)="onHeaderPointerUp($event)"
      (pointercancel)="onHeaderPointerUp($event)"
    >
      <!-- A public-preview guest has no history to list. -->
      @if (!chat.isGuest()) {
        <button
          class="icon-btn"
          (click)="chat.toggleHistory()"
          aria-label="Toggle conversation list"
        >
          <mat-icon>menu</mat-icon>
        </button>
      }
      <span class="sheet-title">
        <tp-vega-avatar [size]="22" />
        Vega
      </span>
      <span class="header-spacer"></span>
      <button class="icon-btn" (click)="chat.newChat()" aria-label="New chat" data-tour-step="global.assistant-new-chat">
        <mat-icon>edit_square</mat-icon>
      </button>
      <!-- Chat-only route in a new tab, full-screen — desktop only; the popout route needs a sign-in, so not for guests. -->
      @if (canPopOut() && !chat.isGuest()) {
        <button
          class="icon-btn"
          (click)="chat.popOut()"
          aria-label="Open in new tab"
          matTooltip="Open full-screen in a new tab"
        >
          <mat-icon>open_in_new</mat-icon>
        </button>
      }
      @if (chat.fullScreen()) {
        <button
          class="icon-btn"
          (click)="chat.exitFullScreen()"
          aria-label="Exit full screen"
          matTooltip="Back to the page this chat was opened from"
        >
          <mat-icon>close_fullscreen</mat-icon>
        </button>
      } @else {
        <!-- Dock to a screen edge at a third of the viewport; the pressed one is
             where the panel currently sits. -->
        <span class="dock-group">
          @for (dock of dockEdges; track dock.edge) {
            <button
              class="icon-btn dock-btn"
              [class.dock-btn--active]="activeDockEdge() === dock.edge"
              [attr.aria-pressed]="activeDockEdge() === dock.edge"
              [attr.aria-label]="dock.label"
              [matTooltip]="dock.label"
              (click)="dockTo(dock.edge)"
            >
              <mat-icon>{{ dock.icon }}</mat-icon>
            </button>
          }
        </span>
      }
      <button class="icon-btn" (click)="chat.close()" aria-label="Close panel">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="sheet-body">
      <!-- Thread history column -->
      @if (chat.showHistory()) {
        <aside class="history">
          <div class="history-list">
            @for (group of historyGroups(); track group.key) {
              <section class="history-day" [class.history-day--saved]="group.saved">
                <button
                  class="history-day-toggle"
                  type="button"
                  [attr.aria-expanded]="group.expanded"
                  [attr.aria-controls]="'history-day-' + group.key"
                  (click)="toggleHistoryGroup(group.key, group.expanded)"
                >
                  <mat-icon [class.history-day-icon--expanded]="group.expanded"
                    >chevron_right</mat-icon
                  >
                  @if (group.saved) {
                    <mat-icon class="history-saved-icon" aria-hidden="true">star</mat-icon>
                  }
                  <span class="history-day-label">{{ group.label }}</span>
                  <span class="history-day-count">{{ group.threads.length }}</span>
                </button>

                @if (group.expanded) {
                  <div class="history-day-items" [id]="'history-day-' + group.key">
                    @for (thread of group.threads; track thread.id) {
                      <div
                        class="history-item"
                        [class.history-item--active]="thread.id === chat.activeThreadId()"
                        (click)="chat.selectThread(thread.id)"
                        (keydown.enter)="chat.selectThread(thread.id)"
                        role="button"
                        tabindex="0"
                      >
                        @if (isGenerating(thread) || chat.isThreadStreaming(thread.id)) {
                          <mat-icon
                            class="history-schedule-icon history-generating-icon"
                            [matTooltip]="
                              isGenerating(thread)
                                ? 'A scheduled run is writing this report'
                                : 'Vega is still answering here'
                            "
                            >autorenew</mat-icon
                          >
                        } @else if (thread.origin === "schedule") {
                          <mat-icon
                            class="history-schedule-icon"
                            matTooltip="Created by a scheduled query"
                            >event_repeat</mat-icon
                          >
                        }
                        <span class="history-title">{{ thread.title }}</span>
                        @if (threadAgo(thread); as ago) {
                          <span class="history-time">{{ ago }}</span>
                        }
                        @if (isGenerating(thread) || chat.isThreadStreaming(thread.id)) {
                          <span class="history-generating-label">generating…</span>
                        } @else if (thread.unread) {
                          <span class="unread-dot" aria-label="Unread"></span>
                        }
                        @if (!isGenerating(thread)) {
                          <button
                            class="icon-btn history-delete"
                            matTooltip="Delete conversation"
                            (click)="$event.stopPropagation(); chat.deleteThread(thread.id)"
                            aria-label="Delete conversation"
                          >
                            <mat-icon>delete_outline</mat-icon>
                          </button>
                        }
                      </div>
                    } @empty {
                      @if (group.saved && chat.threads().length > 0) {
                        <div class="history-saved-empty">No saved conversations</div>
                      }
                    }
                  </div>
                }
              </section>
            }
            @if (chat.threads().length === 0) {
              <div class="history-empty">No conversations yet</div>
            }
          </div>
        </aside>
      }

      <!-- Conversation -->
      <div class="conversation">
        @if (!chat.canChat()) {
          <div class="empty-state">
            <mat-icon>lock</mat-icon>
            <p>Sign in to chat with Vega, the ReTraid assistant.</p>
          </div>
        } @else {
          <div class="messages" #messageScroll (scroll)="onScroll()">
            @if (chat.historyLoading()) {
              <div class="empty-state"><p>Loading conversation…</p></div>
            }
            @if (chat.earlierOmitted() && !chat.historyLoading()) {
              <p class="history-truncated">
                Showing the most recent messages — earlier ones in this
                conversation aren't loaded.
              </p>
            }
            <!-- Popped out mid-turn: the live answer belongs to the tab that
                 asked; this one can only wait for it to be saved. -->
            @if (chat.generatingElsewhere()) {
              <p class="live-elsewhere" role="status">
                Vega is still answering in the tab you opened this
                from — reload here once it finishes.
              </p>
            }
            @if (chat.welcome(); as welcome) {
              @if (!chat.activeThreadId() && chat.messages().length === 0) {
                <!-- App-written greeting (Walkthroughs page): not sent, not saved. -->
                <div class="welcome" role="note">
                  <div class="welcome-speaker">
                    <tp-vega-avatar [size]="20" />
                    <span>Vega</span>
                  </div>
                  <div class="welcome-text">{{ welcome }}</div>
                  @if (hasSuggestions()) {
                    <div class="welcome-chips" role="group" aria-label="Suggested questions">
                      @for (suggestion of pageSuggestions(); track suggestion) {
                        <button type="button" class="suggestion-chip" [disabled]="chat.streaming()" (click)="pickSuggestion(suggestion)">
                          {{ suggestion }}
                        </button>
                      }
                    </div>
                  }
                </div>
              }
            }
            @for (message of chat.messages(); track message.id) {
              <tp-agent-message
                [message]="message"
                [conversationEnd]="$last"
                [showSpeaker]="$first || chat.messages()[$index - 1].role !== 'assistant'"
              />
            }
            @if (chat.error()) {
              <div class="error-row">
                <mat-icon>error_outline</mat-icon>
                <span>{{ chat.error() }}</span>
                @if (chat.canRetry) {
                  <button class="retry-btn" (click)="chat.retry()">Retry</button>
                }
              </div>
            }
            <!-- The public preview's budget is spent: a set-apart note, and the composer below is locked. -->
            @if (chat.guestLimitReached()) {
              <div class="guest-limit" role="status">
                <mat-icon aria-hidden="true">lock_open</mat-icon>
                <span>{{ chat.guestLimitMessage }}</span>
              </div>
            }
          </div>

          <!-- Suggested questions: opens above the composer and adds its own
               height to the panel, so the conversation keeps its space. Sits
               outside the aria-live row so opening it isn't announced. -->
          @if (suggestionsOpen()) {
            <div
              class="suggestions"
              #suggestionsEl
              id="chat-suggestions"
              role="group"
              aria-label="Suggested questions"
              (keydown.escape)="onSuggestionsEscape()"
            >
              @for (suggestion of pageSuggestions(); track suggestion) {
                <button
                  type="button"
                  class="suggestion-chip"
                  [disabled]="chat.streaming()"
                  (click)="pickSuggestion(suggestion)"
                >
                  {{ suggestion }}
                </button>
              }
            </div>
          }

          <footer class="composer" data-tour-step="global.assistant-composer">
            <!-- Model mode: what each mode MEANS is a backend decision; this
                 only carries the user's intent with the next turn. A guest
                 always runs on Fast (pinned by the runtime), so no choice. -->
            @if (!chat.isGuest()) {
              <button
                class="mode-btn"
                type="button"
                [matMenuTriggerFor]="modeMenu"
                [matTooltip]="chatModeHint()"
                aria-label="Answer mode"
              >
                {{ chatModeLabel() }}
                <mat-icon>expand_more</mat-icon>
              </button>
            }
            <mat-menu #modeMenu="matMenu" class="chat-mode-menu">
              @for (option of chatModeOptions; track option.value) {
                <button
                  mat-menu-item
                  type="button"
                  (click)="chat.setChatMode(option.value)"
                  [attr.aria-current]="chat.chatMode() === option.value"
                  [attr.aria-label]="option.label + ': ' + option.hint"
                >
                  <span class="mode-option">
                    <span class="mode-option__label">{{ option.label }}</span>
                    <mat-icon
                      class="mode-option__info"
                      [matTooltip]="option.hint"
                      matTooltipPosition="right"
                      >info_outline</mat-icon
                    >
                    @if (chat.chatMode() === option.value) {
                      <mat-icon class="mode-option__check">check</mat-icon>
                    }
                  </span>
                </button>
              }
            </mat-menu>
            <textarea
              rows="1"
              aria-label="Chat message"
              [ngModel]="draft()"
              (ngModelChange)="draft.set($event)"
              (keydown)="onComposerKeydown($event)"
              [disabled]="chat.guestLimitReached()"
            ></textarea>
            @if (chat.streaming()) {
              <!-- global.assistant-busy: a message was sent and the assistant is answering (walkthrough "ask" steps). -->
              <button class="send-btn stop" (click)="chat.stop()" aria-label="Stop generating" data-tour-step="global.assistant-busy">
                <mat-icon>stop</mat-icon>
              </button>
            } @else {
              <button
                class="send-btn"
                (click)="submit()"
                [disabled]="!draft().trim() || chat.guestLimitReached()"
                aria-label="Send message"
              >
                <mat-icon>arrow_upward</mat-icon>
              </button>
            }
          </footer>

          @if (chat.lookingAtTarget(); as focus) {
            <div class="focus-context" aria-live="polite">
              <mat-icon aria-hidden="true">{{ chat.focusedTarget() ? "ads_click" : "web_asset" }}</mat-icon>
              <span class="focus-context__prefix">Looking at:</span>
              <span class="focus-context__label">{{ focus.label ?? "selected item" }}</span>
              <!-- The bulb is pinned to the far right so it never shifts when
                   the transient clear button comes and goes. -->
              <span class="focus-context__actions">
                @if (chat.focusedTarget()) {
                  <button
                    type="button"
                    class="focus-context__btn focus-context__clear"
                    (click)="chat.clearFocus()"
                    aria-label="Clear selected item context"
                    matTooltip="Return to page context"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                }
                @if (hasSuggestions()) {
                  <button
                    #suggestBulb
                    type="button"
                    class="focus-context__btn focus-context__suggest"
                    [class.focus-context__btn--active]="suggestionsOpen()"
                    [attr.aria-expanded]="suggestionsOpen()"
                    aria-controls="chat-suggestions"
                    aria-label="Suggested questions"
                    [matTooltip]="suggestionsOpen() ? 'Hide suggested questions' : 'Suggested questions for this view'"
                    (click)="toggleSuggestions()"
                  >
                    <mat-icon>lightbulb</mat-icon>
                  </button>
                }
              </span>
            </div>
          }
        }
      </div>
    </div>

    <!-- Pull a side to resize. A free window offers all four; edge-docked, only
         the side facing the page, so the panel stays flush against its edge. -->
    @if (chat.isFloating() && !chat.fullScreen()) {
      @for (handle of resizeEdges(); track handle.edge) {
        <div
          [class]="'resize-edge resize-edge--' + handle.edge"
          role="separator"
          [attr.aria-label]="handle.label"
          [attr.aria-orientation]="handle.orientation"
          (pointerdown)="onResizePointerDown($event, handle.edge)"
          (pointermove)="onResizePointerMove($event)"
          (pointerup)="onResizePointerUp($event)"
          (pointercancel)="onResizePointerUp($event)"
        ></div>
      }
    }
  </section>
}
